'use client'

import { useEffect, useState } from 'react'
import s from './ascii-background.module.css'

// Egyptian hieroglyphic characters to use in the background
const ASCII_CHARS = [
  '𓂀',
  '𓆣',
  '𓅓',
  '𓃰',
  '𓇋',
  '𓉐',
  '𓏏',
  '𓎛',
  '𓈖',
  '𓄿',
  '𓍿',
  '𓎼',
  '𓋴',
  '𓎡',
  '𓎯',
  '𓇼',
  '𓇳',
  '𓆑',
  '𓊪',
  '𓋹',
  '𓇋',
  '𓎢',
  '𓉔',
  '𓉷',
  '𓇾',
  '𓋞',
  '𓅂',
  '𓇒',
  '𓈗',
  '𓆟',
  '𓇥',
  '𓋴',
  '𓉼',
  '𓆙',
  '𓈍',
  '𓆓',
  '𓎠',
  '𓎩',
  '𓎰',
  '𓋔',
  '𓉮',
  '𓍱',
  '𓈲',
  '𓉺',
  '𓅨',
  '𓆇',
  '𓆤',
  '𓊃',
  '𓉽',
]

interface GridCell {
  id: string
  char: string
}

interface GridRow {
  id: string
  cells: GridCell[]
}

export function AsciiBackground() {
  const [grid, setGrid] = useState<GridRow[]>([])

  useEffect(() => {
    // Calculate grid size based on viewport
    const generateGrid = () => {
      // Calculate character size based on viewport breakpoints
      let charSize = 40 // Default mobile size
      if (window.innerWidth >= 1200) {
        charSize = 60
      } else if (window.innerWidth >= 768) {
        charSize = 50
      }

      // Calculate with scale(1.1) transform in mind
      const containerWidth = window.innerWidth * 1.1
      const containerHeight = window.innerHeight * 1.1

      // Calculate columns and rows to fill the entire viewport
      const cols = Math.ceil(containerWidth / charSize) + 1
      const rows = Math.ceil(containerHeight / charSize) + 1

      const timestamp = Date.now()
      const newGrid = Array.from({ length: rows }, (_, rowIndex) => ({
        id: `row-${timestamp}-${rowIndex}`,
        cells: Array.from({ length: cols }, (_, colIndex) => ({
          id: `cell-${timestamp}-${rowIndex}-${colIndex}`,
          char: ASCII_CHARS[Math.floor(Math.random() * ASCII_CHARS.length)],
        })),
      }))

      setGrid(newGrid)
    }

    generateGrid()

    // Regenerate on resize with debounce
    let resizeTimeout: NodeJS.Timeout
    const handleResize = () => {
      clearTimeout(resizeTimeout)
      resizeTimeout = setTimeout(() => {
        generateGrid()
      }, 250)
    }

    window.addEventListener('resize', handleResize)
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(resizeTimeout)
    }
  }, [])

  return (
    <div className={s.container}>
      <div className={s.wrapper}>
        {grid.map((row) =>
          row.cells.map((cell) => (
            <span
              key={cell.id}
              className={s.char}
              style={{
                animationDelay: `${Math.random() * 8}s`,
              }}
            >
              {cell.char}
            </span>
          ))
        )}
      </div>
    </div>
  )
}
